import { useEffect, useRef, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { Box, IconButton, Link, Stack, Typography, useTheme } from '@mui/material'
import { ImageUpIcon, PlusIcon, Trash2Icon, UserCheckIcon, XIcon } from 'lucide-react'
import {
  VERAFIED_EPISODE_STATUS_LABELS,
  VERAFIED_GUEST_STATUSES_ALLOWED_UNDER_CHECK,
  VERAFIED_LINK_STATE_LABELS,
  verafiedLinkKind,
  VERAFIED_LINK_KIND_LABELS,
  type VerafiedConnection,
  type VerafiedGuestStatus,
  type VerafiedGuestType,
  type VerafiedInfluence,
} from '@repo/types'
import { Alert, Avatar, Button, Modal } from '@repo/ui'
import { Search } from 'components/input/Search'

import { Field, FormSubmitButton, SelectField, TextAreaField } from 'components'
import { ConfirmSettingModal } from 'components/feedback/modals/ConfirmSettingModal/ConfirmSettingModal'
import { useSnackbar } from 'contexts'
import { useVerafiedActions, useVerafiedPeopleSearch } from 'hooks'
import type { VerafiedGuest, VerafiedGuestInput, VerafiedPersonMatch } from 'services'

import { CONNECTION_OPTIONS, GUEST_STATUS_OPTIONS, INFLUENCE_OPTIONS, initialsOf } from './verafiedModel'

type Untyped = any

type GuestForm = {
  name: string
  guest_type: VerafiedGuestType
  role_title: string
  company_name: string
  status: VerafiedGuestStatus
  influence: string
  connection: string
  industry: string
  company_type: string
  notes: string
  check_required: string
  links: Array<{ url: string }>
}

const TYPE_OPTIONS = [
  { value: 'person', label: 'Person' },
  { value: 'organisation', label: 'Organisation or band' },
]

const blank = (v: string) => (v.trim() ? v.trim() : null)

const KIND_LABEL: Record<VerafiedPersonMatch['kind'], string> = {
  lead: 'Person',
  sales_account: 'Sales company',
  company: 'Vera company',
}

/**
 * VRG-734 — add or edit a VERAfied guest. ADDING starts with a search of the people and companies
 * we already store (spec §1): pick one and the guest role sits on that record; only when nobody
 * matches does it create a guest of its own. Read-only for anyone who is not platform_admin.
 */
export const GuestFormModal = ({
  open,
  guest,
  canManage,
  onClose,
}: {
  open: boolean
  /** Absent = add. */
  guest: VerafiedGuest | null
  canManage: boolean
  onClose: () => void
}) => {
  const theme = useTheme()
  const { open: notify } = useSnackbar()
  const actions = useVerafiedActions()
  const methods = useForm<GuestForm>({ mode: 'onChange' })
  const { control, reset, handleSubmit, watch } = methods
  const links = useFieldArray({ control, name: 'links' })
  const [q, setQ] = useState('')
  const [picked, setPicked] = useState<VerafiedPersonMatch | null>(null)
  const [skippedSearch, setSkippedSearch] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const search = useVerafiedPeopleSearch(guest ? '' : q)
  const creating = !guest
  const readOnly = !canManage
  const check = watch('check_required') ?? ''

  useEffect(() => {
    if (!open) return
    setQ('')
    setPicked(null)
    setSkippedSearch(false)
    reset({
      name: guest?.name ?? '',
      guest_type: guest?.guest_type ?? 'person',
      role_title: guest?.role_title ?? '',
      company_name: guest?.company_name ?? '',
      status: guest?.status ?? 'not_invited',
      influence: guest?.influence ?? '',
      connection: guest?.connection ?? '',
      industry: guest?.industry ?? '',
      company_type: guest?.company_type ?? '',
      notes: guest?.notes ?? '',
      check_required: guest?.check_required ?? '',
      links: (guest?.links ?? []).map((l) => ({ url: l.url })),
    })
  }, [open, guest, reset])

  const pick = (m: VerafiedPersonMatch) => {
    setPicked(m)
    methods.setValue('name', m.kind === 'lead' ? m.name : m.name, { shouldDirty: true })
    if (m.kind !== 'lead') methods.setValue('company_name', m.name, { shouldDirty: true })
  }

  const statusOptions = GUEST_STATUS_OPTIONS.map((o) => {
    const blocked = !!check.trim() && !VERAFIED_GUEST_STATUSES_ALLOWED_UNDER_CHECK.includes(o.value)
    return blocked ? { ...o, disabled: true, disabledReason: 'Clear the check first' } : o
  })

  const save = handleSubmit((v) => {
    const input: VerafiedGuestInput = {
      name: v.name.trim(),
      guest_type: v.guest_type,
      role_title: blank(v.role_title),
      company_name: blank(v.company_name),
      status: v.status,
      influence: (blank(v.influence) as VerafiedInfluence | null) ?? null,
      connection: (blank(v.connection) as VerafiedConnection | null) ?? null,
      industry: blank(v.industry),
      company_type: blank(v.company_type),
      notes: blank(v.notes),
      check_required: blank(v.check_required),
      links: v.links.map((l) => l.url.trim()).filter(Boolean).map((url) => ({ url })),
      ...(picked
        ? {
            lead_id: picked.kind === 'lead' ? picked.id : null,
            sales_account_id: picked.kind === 'sales_account' ? picked.id : null,
            company_id: picked.kind === 'company' ? picked.id : null,
          }
        : {}),
    }
    const done = () => {
      onClose()
    }
    const fail = (e: unknown) => notify({ type: 'error', text: (e as Error).message })
    if (guest) actions.updateGuest.mutate({ id: guest.id, input }, { onSuccess: done, onError: fail })
    else actions.createGuest.mutate(input, { onSuccess: done, onError: fail })
  })

  const m = methods as Untyped
  const saving = actions.createGuest.isPending || actions.updateGuest.isPending
  // Adding: search first. The form appears once a match is picked or "no match" is chosen.
  const showSearch = creating && !picked && !skippedSearch

  return (
    <>
      <Modal
        title={creating ? 'Add guest' : readOnly ? guest?.name ?? 'Guest' : 'Edit guest'}
        open={open}
        onClose={onClose}
        actions={
          readOnly || showSearch ? (
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          ) : (
            <>
              {guest && (
                <Button
                  variant="secondary"
                  destructive="hover"
                  startIcon={<Trash2Icon size={18} strokeWidth={1} />}
                  onClick={() => setConfirmDelete(true)}
                  sx={{ mr: 'auto' }}
                >
                  Remove guest
                </Button>
              )}
              <Button variant="secondary" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <FormSubmitButton control={control} requireDirty={!!guest} onClick={() => save()} loading={saving}>
                {guest ? 'Save' : 'Add guest'}
              </FormSubmitButton>
            </>
          )
        }
      >
        {showSearch ? (
          <Stack spacing={3} mt={2}>
            <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              Search the people and companies we already have first. A guest is a role on someone we know, so pick them if they are
              here.
            </Typography>
            <Search value={q} onChange={setQ} onClear={() => setQ('')} placeholder="Name, email or company" fullWidth />
            <Stack spacing={1}>
              {(search.data ?? []).map((r) => (
                <Box
                  key={`${r.kind}:${r.id}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => (r.guest_id ? undefined : pick(r))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !r.guest_id) pick(r)
                  }}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.primary[100]}`,
                    cursor: r.guest_id ? 'default' : 'pointer',
                    opacity: r.guest_id ? 0.6 : 1,
                    '&:hover, &:focus-visible': r.guest_id
                      ? {}
                      : { borderColor: theme.palette.brand[600], boxShadow: `0 0 0 3px ${theme.palette.brand['600-6%']}`, outline: 'none' },
                  }}
                >
                  <Stack direction="row" spacing={3} alignItems="center">
                    <Avatar name={r.name} initials={initialsOf(r.name)} size={32} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body3" sx={{ fontWeight: 500 }} noWrap>
                        {r.name}
                      </Typography>
                      <Typography variant="body3" color="text.secondary" noWrap>
                        {KIND_LABEL[r.kind]}
                        {r.detail ? ` · ${r.detail}` : ''}
                      </Typography>
                    </Box>
                    {r.guest_id && (
                      <Typography variant="body3" color="text.secondary">
                        Already a guest
                      </Typography>
                    )}
                  </Stack>
                </Box>
              ))}
              {q.trim().length >= 2 && !search.isLoading && (search.data ?? []).length === 0 && (
                <Typography variant="body3" color="text.secondary">
                  Nobody here matches “{q.trim()}”.
                </Typography>
              )}
            </Stack>
            <Box>
              <Button
                variant="secondary"
                startIcon={<PlusIcon size={18} strokeWidth={1} />}
                onClick={() => {
                  methods.setValue('name', q.trim(), { shouldDirty: true })
                  setSkippedSearch(true)
                }}
              >
                {q.trim() ? `Add “${q.trim()}” as a new guest` : 'Add a new guest'}
              </Button>
            </Box>
          </Stack>
        ) : (
          <Stack spacing={4} mt={2}>
            {(picked || guest?.lead || guest?.sales_account || guest?.company) && (
              <Stack direction="row" spacing={2} alignItems="center" sx={{ color: 'text.secondary' }}>
                <UserCheckIcon size={16} strokeWidth={1} />
                <Typography variant="body3">
                  {picked
                    ? `Linked to ${KIND_LABEL[picked.kind].toLowerCase()} ${picked.name}`
                    : guest?.lead
                      ? `Linked to person ${guest.lead.name ?? guest.lead.email}`
                      : `Linked to ${guest?.company ? 'Vera company' : 'sales company'} ${(guest?.company ?? guest?.sales_account)?.name}`}
                </Typography>
              </Stack>
            )}
            {guest && guest.import_notes.length > 0 && (
              <Alert
                severity="warning"
                description={guest.import_notes.join(' ')}
                button={
                  canManage ? (
                    // No success toast: the notice disappears, which is the confirmation.
                    <Button
                      variant="secondary"
                      onClick={() =>
                        actions.updateGuest.mutate(
                          { id: guest.id, input: { import_notes: [] } },
                          { onError: (e) => notify({ type: 'error', text: (e as Error).message }) },
                        )
                      }
                    >
                      Confirm
                    </Button>
                  ) : undefined
                }
              />
            )}
            {guest && (
              <Stack direction="row" spacing={3} alignItems="center">
                <Avatar src={guest.photo_url} name={guest.name} initials={initialsOf(guest.name)} size={56} />
                {canManage && (
                  <Stack direction="row" spacing={2}>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      hidden
                      onChange={(e) => {
                        const f = e.target.files?.[0]
                        e.target.value = ''
                        if (!f) return
                        actions.uploadGuestPhoto.mutate(
                          { id: guest.id, file: f },
                          {
                            // No success toast: the new photo is the confirmation.
                            onError: (err) => notify({ type: 'error', text: (err as Error).message }),
                          },
                        )
                      }}
                    />
                    <Button
                      variant="secondary"
                      startIcon={<ImageUpIcon size={18} strokeWidth={1} />}
                      loading={actions.uploadGuestPhoto.isPending}
                      onClick={() => fileRef.current?.click()}
                    >
                      {guest.photo_url ? 'Replace photo' : 'Upload photo'}
                    </Button>
                    {guest.photo_url && (
                      <Button variant="secondary" onClick={() => actions.updateGuest.mutate({ id: guest.id, input: { photo_url: null } })}>
                        Remove photo
                      </Button>
                    )}
                  </Stack>
                )}
              </Stack>
            )}
            <Field {...m} name="name" label="Name" readOnly={readOnly} rules={{ required: 'Give the guest a name' }} />
            <Stack direction="row" spacing={3}>
              <Box sx={{ flex: 1 }}>
                <Controller
                  control={control}
                  name="guest_type"
                  render={({ field }) => (
                    <SelectField label="Type" options={TYPE_OPTIONS} value={field.value} disabled={readOnly} onChange={(v) => field.onChange(String(v))} />
                  )}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Controller
                  control={control}
                  name="status"
                  render={({ field }) => (
                    <SelectField label="Status" options={statusOptions} value={field.value} disabled={readOnly} onChange={(v) => field.onChange(String(v))} />
                  )}
                />
              </Box>
            </Stack>
            <Stack direction="row" spacing={3}>
              <Box sx={{ flex: 1 }}>
                <Field {...m} name="role_title" label="Role (optional)" placeholder="CEO" readOnly={readOnly} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Field {...m} name="company_name" label="Company (optional)" readOnly={readOnly} />
              </Box>
            </Stack>
            <Stack direction="row" spacing={3}>
              <Box sx={{ flex: 1 }}>
                <Controller
                  control={control}
                  name="influence"
                  render={({ field }) => (
                    <SelectField
                      label="Influence"
                      placeholder="Not set"
                      options={[{ value: '', label: 'Not set' }, ...INFLUENCE_OPTIONS]}
                      value={field.value}
                      disabled={readOnly}
                      onChange={(v) => field.onChange(String(v))}
                    />
                  )}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Controller
                  control={control}
                  name="connection"
                  render={({ field }) => (
                    <SelectField
                      label="How we know them"
                      placeholder="Not set"
                      options={[{ value: '', label: 'Not set' }, ...CONNECTION_OPTIONS]}
                      value={field.value}
                      disabled={readOnly}
                      onChange={(v) => field.onChange(String(v))}
                    />
                  )}
                />
              </Box>
            </Stack>
            <Stack direction="row" spacing={3}>
              <Box sx={{ flex: 1 }}>
                <Field {...m} name="industry" label="Industry (optional)" placeholder="Fine art" readOnly={readOnly} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Field {...m} name="company_type" label="Company type (optional)" placeholder="Resale platform" readOnly={readOnly} />
              </Box>
            </Stack>

            <Stack spacing={2}>
              <Typography variant="body3" sx={{ fontWeight: 500 }}>
                Profile links
              </Typography>
              {links.fields.map((f, i) => {
                const url = watch(`links.${i}.url`) ?? ''
                return (
                  <Stack key={f.id} direction="row" spacing={2} alignItems="center">
                    <Typography variant="body3" color="text.secondary" sx={{ width: 76, flexShrink: 0 }}>
                      {url.trim() ? VERAFIED_LINK_KIND_LABELS[verafiedLinkKind(url)] : 'Link'}
                    </Typography>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Field
                        {...m}
                        name={`links.${i}.url`}
                        placeholder="https://"
                        readOnly={readOnly}
                        rules={{ pattern: { value: /^https?:\/\/\S+$/i, message: 'A full http(s) link' } }}
                      />
                    </Box>
                    {!readOnly && (
                      <IconButton aria-label="Remove link" onClick={() => links.remove(i)}>
                        <XIcon size={16} strokeWidth={1} />
                      </IconButton>
                    )}
                  </Stack>
                )
              })}
              {!readOnly && (
                <Box>
                  <Button variant="secondary" startIcon={<PlusIcon size={18} strokeWidth={1} />} onClick={() => links.append({ url: '' })}>
                    Add link
                  </Button>
                </Box>
              )}
            </Stack>

            {guest && guest.episodes.length > 0 && (
              <Stack spacing={1}>
                <Typography variant="body3" sx={{ fontWeight: 500 }}>
                  Episodes
                </Typography>
                {guest.episodes.map((e) => (
                  <Typography key={e.id} variant="body3" color="text.secondary">
                    {VERAFIED_EPISODE_STATUS_LABELS[e.status]}: {e.title} ({VERAFIED_LINK_STATE_LABELS[e.state].toLowerCase()})
                  </Typography>
                ))}
                <Typography variant="body3" color="text.secondary">
                  Link guests to an episode from the episode itself.
                </Typography>
              </Stack>
            )}

            <TextAreaField
              {...m}
              name="check_required"
              label="Check before inviting (optional)"
              placeholder="Why this guest needs a reputational or legal check first"
              helperText="While this is set, the guest stays Not invited or Paused."
              readOnly={readOnly}
            />
            <TextAreaField
              {...m}
              name="notes"
              label="Notes (internal)"
              placeholder="How we know them"
              helperText="Never exported, never shown outside these internal pages."
              readOnly={readOnly}
            />
            {guest?.links.length ? (
              <Stack direction="row" spacing={3} useFlexGap sx={{ flexWrap: 'wrap' }}>
                {guest.links.map((l) => (
                  <Link key={l.id} href={l.url} target="_blank" rel="noopener noreferrer" variant="body3">
                    Open {VERAFIED_LINK_KIND_LABELS[l.kind]}
                  </Link>
                ))}
              </Stack>
            ) : null}
          </Stack>
        )}
      </Modal>
      <ConfirmSettingModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={`Remove ${guest?.name ?? 'this guest'}?`}
        consequence="This removes the guest role, its profile links and its episode links. A person or company record it was linked to is not touched."
        confirmLabel="Remove guest"
        loading={actions.deleteGuest.isPending}
        onConfirm={() => {
          if (!guest) return
          actions.deleteGuest.mutate(guest.id, {
            onSuccess: () => {
              setConfirmDelete(false)
              onClose()
            },
            onError: (e) => notify({ type: 'error', text: (e as Error).message }),
          })
        }}
      />
    </>
  )
}
